import type { ReactNode } from "react";
import {
  centsToDollarInput,
  dollarInputToCents,
  type OpsObligationDetails,
  type OpsObligationKind,
} from "@/lib/opsObligations/opsObligationFields";

const fieldClass =
  "mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 dark:border-zinc-600 dark:bg-zinc-900";

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return <label className="block text-sm text-gray-700 dark:text-zinc-300">{label}{children}</label>;
}

export function OpsObligationKindFields(props: {
  kind: OpsObligationKind;
  details: OpsObligationDetails;
  onChange: (next: OpsObligationDetails) => void;
}) {
  const d = props.details;
  function set<K extends keyof OpsObligationDetails>(key: K, value: OpsObligationDetails[K]) {
    props.onChange({ ...d, [key]: value });
  }

  if (props.kind === "internet") {
    return (
      <section className="space-y-3 rounded border border-gray-200 p-3 dark:border-zinc-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Internet details</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Plan name">
            <input className={fieldClass} value={d.plan_name ?? ""} onChange={(e) => set("plan_name", e.target.value || null)} />
          </Field>
          <Field label="Download Mbps">
            <input
              className={fieldClass}
              value={d.download_mbps ?? ""}
              onChange={(e) => set("download_mbps", e.target.value === "" ? null : Number(e.target.value))}
            />
          </Field>
          <Field label="Upload Mbps">
            <input
              className={fieldClass}
              value={d.upload_mbps ?? ""}
              onChange={(e) => set("upload_mbps", e.target.value === "" ? null : Number(e.target.value))}
            />
          </Field>
          <Field label="Equipment fee (USD)">
            <input
              className={fieldClass}
              value={centsToDollarInput(d.equipment_fee_cents)}
              onChange={(e) => set("equipment_fee_cents", dollarInputToCents(e.target.value))}
            />
          </Field>
          <Field label="Promo price (USD)">
            <input
              className={fieldClass}
              value={centsToDollarInput(d.promo_price_cents)}
              onChange={(e) => set("promo_price_cents", dollarInputToCents(e.target.value))}
            />
          </Field>
          <Field label="Promo ends">
            <input
              className={fieldClass}
              type="date"
              value={d.promo_ends_on ?? ""}
              onChange={(e) => set("promo_ends_on", e.target.value || null)}
            />
          </Field>
          <Field label="Contract ends">
            <input
              className={fieldClass}
              type="date"
              value={d.contract_ends_on ?? ""}
              onChange={(e) => set("contract_ends_on", e.target.value || null)}
            />
          </Field>
          <Field label="Cancellation terms">
            <textarea
              className={fieldClass}
              rows={2}
              value={d.cancellation_terms ?? ""}
              onChange={(e) => set("cancellation_terms", e.target.value || null)}
            />
          </Field>
        </div>
      </section>
    );
  }

  if (props.kind === "lease") {
    return (
      <section className="space-y-3 rounded border border-gray-200 p-3 dark:border-zinc-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Lease details</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Landlord / lessor">
            <input className={fieldClass} value={d.landlord ?? ""} onChange={(e) => set("landlord", e.target.value || null)} />
          </Field>
          <Field label="Tenant entity">
            <input className={fieldClass} value={d.tenant_entity ?? ""} onChange={(e) => set("tenant_entity", e.target.value || null)} />
          </Field>
          <Field label="Base rent (USD)">
            <input
              className={fieldClass}
              value={centsToDollarInput(d.base_rent_cents)}
              onChange={(e) => set("base_rent_cents", dollarInputToCents(e.target.value))}
            />
          </Field>
          <Field label="Deposit (USD)">
            <input
              className={fieldClass}
              value={centsToDollarInput(d.deposit_cents)}
              onChange={(e) => set("deposit_cents", dollarInputToCents(e.target.value))}
            />
          </Field>
          <Field label="Renewal notice deadline">
            <input
              className={fieldClass}
              value={d.renewal_notice_deadline ?? ""}
              onChange={(e) => set("renewal_notice_deadline", e.target.value || null)}
              placeholder="180 days before expiration"
            />
          </Field>
          <Field label="Renewal options">
            <textarea
              className={fieldClass}
              rows={2}
              value={d.renewal_options ?? ""}
              onChange={(e) => set("renewal_options", e.target.value || null)}
            />
          </Field>
          <Field label="Escalation terms">
            <textarea
              className={fieldClass}
              rows={2}
              value={d.escalation_terms ?? ""}
              onChange={(e) => set("escalation_terms", e.target.value || null)}
            />
          </Field>
        </div>
      </section>
    );
  }

  if (props.kind === "mortgage") {
    return (
      <section className="space-y-3 rounded border border-gray-200 p-3 dark:border-zinc-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Mortgage details</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Lender / servicer">
            <input className={fieldClass} value={d.lender ?? ""} onChange={(e) => set("lender", e.target.value || null)} />
          </Field>
          <Field label="Borrower entity">
            <input className={fieldClass} value={d.borrower_entity ?? ""} onChange={(e) => set("borrower_entity", e.target.value || null)} />
          </Field>
          <Field label="Loan / account number">
            <input className={fieldClass} value={d.loan_number ?? ""} onChange={(e) => set("loan_number", e.target.value || null)} />
          </Field>
          <Field label="Interest rate (bps)">
            <input
              className={fieldClass}
              value={d.interest_rate_bps ?? ""}
              onChange={(e) => set("interest_rate_bps", e.target.value === "" ? null : Number(e.target.value))}
              placeholder="625 = 6.25%"
            />
          </Field>
          <Field label="Origination">
            <input
              className={fieldClass}
              type="date"
              value={d.origination_on ?? ""}
              onChange={(e) => set("origination_on", e.target.value || null)}
            />
          </Field>
          <Field label="Maturity">
            <input
              className={fieldClass}
              type="date"
              value={d.maturity_on ?? ""}
              onChange={(e) => set("maturity_on", e.target.value || null)}
            />
          </Field>
        </div>
      </section>
    );
  }

  return null;
}
